<template>
    <!-- A button that opens a short list of actions (a row's ⋮ menu, or a
         split button like Run Report). Closes on a pick, an outside click or
         Escape. Each item: {label, icon?, danger?, disabled?, onSelect}. -->
    <div    ref="root"
            class="relative inline-block text-left"
    >
        <slot   name="trigger"
                :open="open"
                :toggle="toggle"
        >
            <button type="button"
                    class="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    :aria-label="label"
                    aria-haspopup="menu"
                    :aria-expanded="open"
                    @click="toggle"
            >
                <tabler-icon    name="dotsVertical"
                                size-class="h-5 w-5"
                ></tabler-icon>
            </button>
        </slot>

        <!-- On <body>, positioned from the trigger, so a table or card that
             clips its overflow cannot cut the menu off. -->
        <teleport to="body">
            <div    v-if="open"
                    ref="menu"
                    role="menu"
                    class="fixed z-[70] min-w-[11rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg"
                    :style="position"
            >
                <button v-for="item in items"
                        :key="item.label"
                        type="button"
                        role="menuitem"
                        :disabled="item.disabled"
                        class="flex w-full items-center gap-2 whitespace-nowrap px-3 py-2 text-left text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        :class="item.danger ? 'text-red-600' : 'text-gray-700'"
                        @click="select(item)"
                >
                    <tabler-icon    v-if="item.icon"
                                    :name="item.icon"
                                    size-class="h-4 w-4"
                    ></tabler-icon>
                    {{ item.label }}
                </button>
            </div>
        </teleport>
    </div>
</template>

<script>
import TablerIcon from "./TablerIcon.vue"

export default {
    name: "ActionMenu",
    components: {TablerIcon},
    props: {
        items: {
            type: Array,
            required: true,
        },
        // Accessible name for the default ⋮ trigger.
        label: {
            type: String,
            default: 'Actions',
        },
    },
    data() {
        return {
            open: false,
            position: {},
        }
    },
    beforeUnmount() {
        this.stopListening()
    },
    methods: {
        toggle() {
            this.open ? this.close() : this.show()
        },
        show() {
            const rect = this.$refs.root.getBoundingClientRect()
            // Right-aligned under the trigger, or above it near the bottom.
            const below = rect.bottom < window.innerHeight * (2 / 3)
            this.position = {
                right: `${window.innerWidth - rect.right}px`,
                ...(below ? {top: `${rect.bottom + 4}px`} : {bottom: `${window.innerHeight - rect.top + 4}px`}),
            }
            this.open = true
            document.addEventListener('click', this.onOutsideClick, true)
            document.addEventListener('keydown', this.onKeydown)
            // A fixed menu would drift from its trigger on scroll.
            window.addEventListener('scroll', this.close, true)
            window.addEventListener('resize', this.close)
        },
        close() {
            this.open = false
            this.stopListening()
        },
        stopListening() {
            document.removeEventListener('click', this.onOutsideClick, true)
            document.removeEventListener('keydown', this.onKeydown)
            window.removeEventListener('scroll', this.close, true)
            window.removeEventListener('resize', this.close)
        },
        onOutsideClick(event) {
            if (! this.$refs.root?.contains(event.target) && ! this.$refs.menu?.contains(event.target)) {
                this.close()
            }
        },
        onKeydown(event) {
            if (event.key === 'Escape') {
                this.close()
            }
        },
        select(item) {
            if (item.disabled) {
                return
            }
            this.close()
            item.onSelect()
        },
    },
}
</script>
